// src/services/authStore.ts
// In-memory auth store with optional sessionStorage hydration for resilience across reloads
export type AuthStoreShape = {
  authToken?: string | null;
  pendingUsername?: string | null;
  userData?: Record<string, any> | null; // { username, authUserId, nameOfUser, ... }
  workspaceData?: Record<string, any> | null; // { workspaceId, userId, role, ... }
};

let store: AuthStoreShape = {
  authToken: null,
  pendingUsername: null,
  userData: null,
  workspaceData: null,
};

const listeners = new Set<(s: AuthStoreShape) => void>();

// Try to hydrate minimal fields from sessionStorage for reload resilience
try {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    const rawUser = sessionStorage.getItem('userData');
    if (rawUser) store.userData = JSON.parse(rawUser);
    const rawWorkspace = sessionStorage.getItem('workspaceData');
    if (rawWorkspace) store.workspaceData = JSON.parse(rawWorkspace);
    const pending = sessionStorage.getItem('pendingUsername');
    if (pending) store.pendingUsername = pending;
    // authToken intentionally NOT hydrated from sessionStorage/localStorage by default
  }
} catch (e) {
  // ignore any parse/storage errors
}

export function getAuthStore(): AuthStoreShape {
  return store;
}

export function setAuthStore(partial: Partial<AuthStoreShape>) {
  store = { ...store, ...partial };
  // notify listeners
  listeners.forEach((fn) => {
    try { fn(store); } catch (_) {}
  });
}

export function clearAuthStore() {
  store = { authToken: null, pendingUsername: null, userData: null, workspaceData: null };
  listeners.forEach((fn) => {
    try { fn(store); } catch (_) {}
  });
}

export function subscribeAuthStore(fn: (s: AuthStoreShape) => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
